import React, { useMemo, useRef, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './HTMLEditor.css';
import { useField } from 'formik';

const HTMLEditor = ({
    name,
    label,
    placeholder = "Enter content...",
    required = false,
    height = 500,
    ...props
}) => {
    const [field, meta, helpers] = useField(name);
    const quillRef = useRef(null);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const isInternalUpdate = useRef(false);

    // Quill modules configuration
    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean']
        ],
        clipboard: {
            matchVisual: false,
        }
    }), []);

    // Quill formats configuration
    const formats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script', 'blockquote', 'code-block',
        'list', 'bullet', 'indent',
        'direction', 'align',
        'link', 'image', 'video'
    ];

    // Handle content change
    const handleChange = (content) => {
        isInternalUpdate.current = true;
        helpers.setValue(content);
        helpers.setTouched(true);
        // Reset the flag after a short delay
        setTimeout(() => {
            isInternalUpdate.current = false;
        }, 100);
    };

    // Handle blur
    const handleBlur = () => {
        helpers.setTouched(true);
    };

    // Handle editor ready state
    const handleEditorReady = () => {
        setIsEditorReady(true);
    };

    // Handle focus to prevent jumping to top
    const handleFocus = () => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            if (quill) {
                // Get current selection or set to end of content
                const length = quill.getLength();
                quill.setSelection(length - 1, 0);
            }
        }
        handleEditorReady();
    };

    // Handle click to maintain cursor position
    const handleClick = (e) => {
        e.preventDefault();
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            if (quill) {
                // Let the default click behavior happen first
                setTimeout(() => {
                    const selection = quill.getSelection();
                    if (!selection || selection.index === null) {
                        const length = quill.getLength();
                        quill.setSelection(length - 1, 0);
                    }
                }, 0);
            }
        }
    };

    // Initialize editor content when it becomes ready (only once)
    useEffect(() => {
        if (isEditorReady && quillRef.current && field.value) {
            const quill = quillRef.current.getEditor();
            if (quill) {
                // Set content without triggering change event
                quill.root.innerHTML = field.value;
            }
        }
    }, [isEditorReady]);

    // Update content only when switching from simple editor (external changes)
    useEffect(() => {
        if (quillRef.current && isEditorReady && !isInternalUpdate.current) {
            const quill = quillRef.current.getEditor();
            if (quill) {
                const currentContent = quill.root.innerHTML;
                const newContent = field.value || '';

                // Only update if content is actually different and not empty
                if (currentContent !== newContent && newContent) {
                    // Store current selection
                    const selection = quill.getSelection();

                    // Update content
                    quill.root.innerHTML = newContent;

                    // Restore selection if it was valid, otherwise set to end
                    if (selection && selection.index !== null) {
                        setTimeout(() => {
                            quill.setSelection(selection.index, selection.length);
                        }, 0);
                    } else {
                        setTimeout(() => {
                            const length = quill.getLength();
                            quill.setSelection(length - 1, 0);
                        }, 0);
                    }
                }
            }
        }
    }, [field.value, isEditorReady]);

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label
                    htmlFor={name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* Editor Container */}
            <div
                className={`border rounded-lg transition-all duration-200 overflow-hidden ${meta.touched && meta.error
                    ? 'border-red-500 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/30'
                    : 'border-gray-300 hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50'
                    }`}
                style={{ height: `${height}px` }}
            >
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={field.value || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    modules={modules}
                    formats={formats}
                    placeholder={placeholder}
                    className="quill-editor"
                    style={{
                        height: '100%',
                        border: 'none'
                    }}
                    onFocus={handleFocus}
                    onClick={handleClick}
                    {...props}
                />
            </div>

            {/* Error Message */}
            {meta.touched && meta.error && (
                <p
                    id={`${name}-error`}
                    className="mt-1 text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {meta.error}
                </p>
            )}

            {/* Character and Word Count */}
            <div className="mt-1 text-xs text-gray-500 text-right">
                {(() => {
                    const text = field.value ? field.value.replace(/<[^>]*>/g, '') : '';
                    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
                    return `${text.length} characters, ${words} words`;
                })()}
            </div>
        </div>
    );
};

export default HTMLEditor;
